import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

/**
 * Browser-native install banner for the QuickXKart PWA.
 *
 * Behaviour:
 *  - Android / desktop Chrome / Edge: uses the `beforeinstallprompt` event.
 *  - iOS Safari: no such event; show static instructions to "Add to Home Screen".
 *  - Dismiss is sticky for 14 days via localStorage. Successful install hides forever.
 *  - Hidden if the app is already running standalone (display-mode: standalone).
 *
 * Mounted once inside RootLayout so it appears site-wide.
 */

const DISMISS_KEY = 'qxk:install-dismissed-at';
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 6000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-expect-error iOS Safari non-standard
    window.navigator.standalone === true
  );
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    const ageMs = Date.now() - dismissedAt;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    let showTimer: number | undefined;

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      deferredRef.current = event as BeforeInstallPromptEvent;
      showTimer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    const onInstalled = () => {
      // Permanent dismiss — large future timestamp so it never re-shows.
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
      } catch {
        // Storage unavailable — non-fatal.
      }
      setVisible(false);
      deferredRef.current = null;
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari never fires beforeinstallprompt — show the manual hint instead.
    if (isIOS()) {
      setIosMode(true);
      showTimer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    }

    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    const ev = deferredRef.current;
    if (!ev) return;
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      if (choice.outcome === 'accepted') {
        // `appinstalled` will fire and finalize the persistent dismiss.
      } else {
        dismiss();
      }
    } finally {
      deferredRef.current = null;
    }
  }, [dismiss]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="qxk-install-title"
      aria-describedby="qxk-install-desc"
      className={cn(
        'fixed inset-x-3 bottom-3 z-[1400] mx-auto max-w-md',
        'rounded-2xl border border-white/10 bg-[color:rgba(11,14,40,0.92)] backdrop-blur-xl',
        'shadow-[0_18px_60px_rgba(0,0,0,0.55)]',
        'p-3 sm:p-4',
        'animate-[qxk-install-slide_360ms_cubic-bezier(.2,.7,.2,1)]'
      )}
    >
      <style>{`
        @keyframes qxk-install-slide {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="flex items-start gap-3">
        <img
          src="/icon-192.png"
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 flex-shrink-0 rounded-xl"
          loading="lazy"
          decoding="async"
        />
        <div className="min-w-0 flex-1">
          <p
            id="qxk-install-title"
            className="font-[family-name:var(--font-display)] text-base uppercase tracking-wide text-white"
          >
            Install QuickXKart App
          </p>
          <p
            id="qxk-install-desc"
            className="mt-0.5 text-[13px] leading-snug text-[color:var(--color-text-secondary,#c8c8d6)]"
          >
            {iosMode
              ? 'Tap the Share icon, then “Add to Home Screen” for the full app experience.'
              : 'One-tap install — faster loads, offline cart, push offers.'}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {!iosMode && (
              <Button size="sm" variant="gold" onClick={install} noMagnetic>
                Install
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss} noMagnetic>
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="ml-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[color:var(--color-text-secondary,#c8c8d6)] transition hover:bg-white/10 hover:text-white"
        >
          <svg
            viewBox="0 0 16 16"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
        </button>
      </div>
    </div>
  );
}
